import { useCallback, useEffect, useState } from "react";
import {
  Star,
  Flag,
  Trash2,
  X,
  Send,
  ChevronDown,
  MapPin,
} from "lucide-react";
import type { Rating, ReportReason, Spot, SpotPhoto } from "@/lib/types";
import {
  getRatingsForSpot,
  upsertRating,
  deleteRating,
  deleteSpot,
  fileReport,
  listSpotPhotos,
  getSpotPhotoUrl,
} from "@/lib/api";
import type { User } from "@supabase/supabase-js";
import { t } from "@/lib/i18n";

/**
 * Bottom sheet showing spot info, ratings list, and rate/report actions
 * (spec 06 §2 screen 2). German UI; no strings hardcoded.
 */
export interface SpotDetailProps {
  spot: Spot;
  user: User | null;
  userLocation: { lng: number; lat: number } | null;
  onClose: () => void;
  /** Require sign-in to rate/report; parent opens the auth modal. */
  onRequireAuth: () => void;
}

export function SpotDetail({
  spot,
  user,
  userLocation,
  onClose,
  onRequireAuth,
}: SpotDetailProps) {
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [photos, setPhotos] = useState<SpotPhoto[]>([]);
  const [loadingRatings, setLoadingRatings] = useState(true);
  const [myStars, setMyStars] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState<ReportReason>("illegal_zone");
  const [reportNote, setReportNote] = useState("");
  const [reportSent, setReportSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load ratings for this spot.
  const loadRatings = useCallback(async () => {
    setLoadingRatings(true);
    try {
      const r = await getRatingsForSpot(spot.id);
      setRatings(r);
      const mine = r.find((x) => x.created_by === user?.id);
      if (mine) {
        setMyStars(mine.stars);
        setMyComment(mine.comment);
      }
    } catch {
      setError(t("errors.loadFailed"));
    } finally {
      setLoadingRatings(false);
    }
  }, [spot.id, user?.id]);

  useEffect(() => {
    void loadRatings();
  }, [loadRatings]);

  // Load photos (anon-readable). Best-effort — a failure just shows no photos.
  useEffect(() => {
    let cancelled = false;
    listSpotPhotos(spot.id)
      .then((p) => {
        if (!cancelled) setPhotos(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [spot.id]);

  const distance = userLocation
    ? haversine(
        userLocation.lat,
        userLocation.lng,
        spot.location.lat,
        spot.location.lng,
      )
    : null;

  const submitRating = async () => {
    if (!user) return onRequireAuth();
    if (myStars < 1) return;
    setSubmitting(true);
    setError(null);
    try {
      await upsertRating({ spot_id: spot.id, stars: myStars, comment: myComment });
      await loadRatings();
      setMyComment("");
    } catch (e) {
      setError(e instanceof Error ? e.message : t("errors.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const removeMyRating = async () => {
    if (!user) return;
    setSubmitting(true);
    try {
      await deleteRating(spot.id);
      setMyStars(0);
      setMyComment("");
      await loadRatings();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("errors.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const submitReport = async () => {
    if (!user) return onRequireAuth();
    setSubmitting(true);
    try {
      await fileReport({
        target_type: "spot",
        target_id: spot.id,
        reason: reportReason,
        note: reportNote,
      });
      setReportSent(true);
      setReportOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("errors.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 sheet-up">
      {/* Backdrop (tap to close) */}
      <button
        type="button"
        aria-label={t("create.cancel")}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 fade-in"
      />
      <div
        className="relative mx-auto max-h-[90vh] max-w-lg overflow-y-auto rounded-t-[var(--radius-sheet)] bg-bg-elevated p-5 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] shadow-2xl"
        role="dialog"
        aria-modal="true"
      >
        <div className="mx-auto mb-3 sheet-grabber" />

        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="text-xs uppercase tracking-wide text-fg-subtle">
              {t(`spot.kind.${spot.kind}`)}
            </span>
            <h2 className="truncate text-xl font-bold text-fg">{spot.name}</h2>
            {distance != null ? (
              <p className="mt-0.5 flex items-center gap-1 text-sm text-fg-muted">
                <MapPin size={13} />
                {t("spot.distanceFromYou", {
                  distance: formatDistance(distance),
                })}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-3 text-fg-muted hover:bg-surface"
            aria-label={t("create.cancel")}
          >
            <X size={20} />
          </button>
        </div>

        {/* Description */}
        {spot.description ? (
          <p className="mt-3 text-sm text-fg-muted">{spot.description}</p>
        ) : null}

        {/* Pending banner: visible only to the creator of a not-yet-reviewed spot. */}
        {spot.status === "pending" && user?.id === spot.created_by ? (
          <p className="mt-3 rounded-lg bg-legal-amber/10 p-2.5 text-xs text-legal-amber">
            {t("create.pendingSelfNotice")}
          </p>
        ) : null}

        {/* Photos */}
        {photos.length > 0 ? (
          <div className="mt-3">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {photos.map((p) => (
                <a
                  key={p.id}
                  href={getSpotPhotoUrl(p.path)}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0"
                >
                  <img
                    src={getSpotPhotoUrl(p.path)}
                    alt=""
                    loading="lazy"
                    className="h-24 w-24 rounded-lg object-cover ring-1 ring-line"
                  />
                </a>
              ))}
            </div>
            {/* CC license attribution for photos that have it */}
            {photos.some((p) => p.license) ? (
              <div className="mt-1.5 space-y-0.5">
                {photos
                  .filter((p) => p.license)
                  .map((p) => (
                    <p key={p.id} className="text-[10px] leading-tight text-fg-subtle">
                      ©{" "}
                      {p.source_url ? (
                        <a
                          href={p.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-fg-muted"
                        >
                          {p.author || "Unbekannt"}
                        </a>
                      ) : (
                        p.author || "Unbekannt"
                      )}
                      {" · "}
                      {p.license}
                    </p>
                  ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Rating summary */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Star size={18} className="fill-legal-amber text-legal-amber" />
            <span className="font-semibold">
              {spot.rating_avg > 0 ? spot.rating_avg.toFixed(1) : "—"}
            </span>
          </div>
          <span className="text-sm text-fg-muted">
            {spot.rating_count > 0
              ? t("spot.rating.count", { count: String(spot.rating_count) })
              : t("spot.rating.none")}
          </span>
        </div>

        {/* Rate form */}
        <div className="mt-4 rounded-xl bg-surface p-3">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setMyStars(n)}
                aria-label={t("spot.rating.stars", { n: String(n) })}
                className="rounded p-1"
              >
                <Star
                  size={26}
                  className={
                    n <= myStars
                      ? "fill-legal-amber text-legal-amber"
                      : "text-fg-subtle"
                  }
                />
              </button>
            ))}
            {myStars > 0 && user ? (
              <button
                type="button"
                onClick={removeMyRating}
                className="ml-auto rounded px-2 py-1.5 text-xs text-fg-subtle hover:text-legal-red"
              >
                {t("spot.rating.delete")}
              </button>
            ) : null}
          </div>
          <textarea
            value={myComment}
            onChange={(e) => setMyComment(e.target.value)}
            maxLength={300}
            placeholder={t("spot.rating.commentPlaceholder")}
            rows={2}
            className="mt-2 w-full resize-none rounded-lg bg-bg p-2 text-sm text-fg outline-none ring-1 ring-line focus:ring-accent"
          />
          <button
            type="button"
            disabled={submitting || myStars < 1}
            onClick={submitRating}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2 text-sm font-semibold text-accent-contrast disabled:opacity-40"
          >
            <Send size={15} />
            {t("spot.rating.submit")}
          </button>
        </div>

        {/* Ratings list */}
        <div className="mt-4">
          <h3 className="mb-2 text-sm font-semibold text-fg">
            {t("spot.rating.label")}
          </h3>
          {loadingRatings ? (
            <div className="h-px w-full animate-pulse bg-line" />
          ) : ratings.length === 0 ? (
            <p className="text-sm text-fg-subtle">{t("spot.rating.none")}</p>
          ) : (
            <ul className="space-y-2">
              {ratings.map((r) => (
                <li key={r.created_by} className="rounded-lg bg-surface p-2.5">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={12}
                        className={
                          i < r.stars
                            ? "fill-legal-amber text-legal-amber"
                            : "text-fg-subtle"
                        }
                      />
                    ))}
                    <span className="ml-auto text-xs text-fg-subtle">
                      {timeAgo(new Date(r.created_at))}
                    </span>
                  </div>
                  {r.comment ? (
                    <p className="mt-1 text-sm text-fg-muted">{r.comment}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Report */}
        {reportSent ? (
          <p className="mt-3 text-sm text-legal-green">{t("spot.report.sent")}</p>
        ) : null}
        {reportOpen ? (
          <div className="mt-3 rounded-xl bg-surface p-3">
            <select
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value as ReportReason)}
              className="w-full rounded-lg bg-bg p-2 text-sm text-fg ring-1 ring-line"
            >
              {(
                ["illegal_zone", "spam", "offensive", "wrong_location", "other"] as ReportReason[]
              ).map((r) => (
                <option key={r} value={r}>
                  {t(`spot.report.reason.${r}`)}
                </option>
              ))}
            </select>
            <input
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              maxLength={300}
              placeholder={t("spot.report.notePlaceholder")}
              className="mt-2 w-full rounded-lg bg-bg p-2 text-sm text-fg ring-1 ring-line"
            />
            <button
              type="button"
              disabled={submitting}
              onClick={submitReport}
              className="mt-2 w-full rounded-lg bg-legal-red/20 py-2 text-sm font-semibold text-legal-red disabled:opacity-40"
            >
              {t("spot.report.submit")}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => (user ? setReportOpen(true) : onRequireAuth())}
            className="mt-3 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-fg-subtle hover:text-legal-red"
          >
            <Flag size={14} />
            {t("spot.report.label")}
          </button>
        )}

        {error ? (
          <p className="mt-3 text-sm text-legal-red">{error}</p>
        ) : null}

        {/* Owner: delete own spot (soft-delete → status removed). */}
        {user?.id === spot.created_by ? (
          <button
            type="button"
            onClick={async () => {
              // Best-effort: parent refetches on close.
              try {
                await deleteSpot(spot.id);
                onClose();
              } catch (e) {
                setError(e instanceof Error ? e.message : t("errors.generic"));
              }
            }}
            className="mt-3 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-legal-red"
          >
            <Trash2 size={14} />
            {t("contributions.deleteSpot")}
          </button>
        ) : null}

        <button
          type="button"
          onClick={onClose}
          className="mx-auto mt-4 flex h-10 items-center gap-1 px-4 text-sm text-fg-subtle"
        >
          <ChevronDown size={16} />
        </button>
      </div>
    </div>
  );
}

// ---------- distance / time helpers ----------

/** Haversine great-circle distance in metres. */
function haversine(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(metres: number): string {
  if (metres >= 1000) return `${(metres / 1000).toFixed(1)} km`;
  return `${Math.round(metres)} m`;
}

/** Relative time ago in German (short form). */
function timeAgo(date: Date): string {
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 60) return t("time.justNow");
  const min = Math.floor(sec / 60);
  if (min < 60) return t("time.minutes", { n: String(min) });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("time.hours", { n: String(hr) });
  const day = Math.floor(hr / 24);
  if (day < 7) return t("time.days", { n: String(day) });
  return t("time.weeks", { n: String(Math.floor(day / 7)) });
}
